/**
 * FotosInput — fotos da solicitação com compressão no cliente (canvas,
 * máx. 1280px no maior lado, JPEG qualidade 0,72). Duas origens:
 *   - "Tirar foto": câmera DENTRO da página (getUserMedia — ver CameraCaptura);
 *   - "Galeria": input SEM capture (abre a galeria do aparelho; aceita
 *     várias de uma vez até o limite).
 *
 * Por que a câmera não usa mais `<input capture>` como caminho principal
 * (bug de produção 2026-07-30): no Chromium para Android, se o app Chrome está
 * sem a permissão `android.permission.CAMERA`, o caminho `capture` aborta em
 * silêncio (`SelectFileDialog.java`: `if (mCapture) { onFileNotSelected(); }`)
 * — sem mensagem, sem seletor, sem evento `change`. O botão virava um no-op,
 * o que atingiu principalmente aparelhos Xiaomi/MIUI (a MIUI revoga sozinha
 * permissões de apps pouco usados). O `<input capture>` continua como
 * fallback para navegadores sem getUserMedia.
 *
 * Regra desta tela: o usuário SEMPRE consegue concluir a solicitação sem mexer
 * em configuração do aparelho — se a câmera não abrir, caímos na galeria (que
 * não exige permissão nenhuma) com a instrução do que fazer.
 *
 * ESTE COMPONENTE É O DONO ÚNICO DO MediaStream: adquire, troca e encerra. O
 * overlay é só apresentação. Concentrar isso aqui é o que garante que a câmera
 * do aparelho nunca fique presa (LED aceso, "câmera em uso" nos outros apps).
 *
 * Props:
 * - `fotos`: [{ uid, imagem (dataURL), legenda }]
 * - `onChange(fotos)`
 * - `max` (default 3)
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiCamera, FiImage, FiX } from "react-icons/fi";

import { useToast } from "../../../context/ToastContext";
import CameraCaptura, {
  cameraEmbutidaDisponivel, ehPermissaoNegada, mensagemDeErroCamera,
} from "./CameraCaptura";
import { gerarUid } from "./utils";
import {
  AvisoCamera, FotoAddBtn, FotoRemoveBtn, FotoThumbBox, FotosWrap, Hint,
  LinkCameraSistema,
} from "./styles";

const LADO_MAXIMO_PX = 1280;
const QUALIDADE_JPEG = 0.72;

/**
 * `ideal` (nunca `exact`): pede Full HD e deixa o aparelho cair no mais próximo
 * que tiver. SEM isto o navegador entrega o default de 640x480 — VGA é pouco
 * para enxergar um defeito numa peça de MDF.
 */
function restricoesDeVideo(facing) {
  return {
    video: {
      facingMode: { ideal: facing },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
    },
    audio: false,
  };
}

function lerComoDataUrl(arquivo) {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(leitor.result);
    leitor.onerror = () => reject(new Error("Falha ao ler a foto."));
    leitor.readAsDataURL(arquivo);
  });
}

function carregarImagem(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Foto inválida."));
    img.src = dataUrl;
  });
}

/**
 * Compressão comum às duas origens (arquivo e vídeo da câmera): reduz o maior
 * lado a LADO_MAXIMO_PX e devolve JPEG. `fonte` é qualquer coisa que o canvas
 * saiba desenhar — <img> ou <video>.
 */
function comprimirFonte(fonte, larguraNatural, alturaNatural) {
  const escala = Math.min(
    1,
    LADO_MAXIMO_PX / Math.max(larguraNatural, alturaNatural),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(larguraNatural * escala));
  canvas.height = Math.max(1, Math.round(alturaNatural * escala));
  canvas.getContext("2d").drawImage(fonte, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", QUALIDADE_JPEG);
}

async function comprimirFoto(arquivo) {
  const img = await carregarImagem(await lerComoDataUrl(arquivo));
  return comprimirFonte(img, img.width, img.height);
}

export default function FotosInput({ fotos, onChange, max = 3 }) {
  const inputCameraRef = useRef(null);
  const inputGaleriaRef = useRef(null);
  const [processando, setProcessando] = useState(false);
  const [avisoCamera, setAvisoCamera] = useState("");
  const toast = useToast();

  // ----- estado da câmera embutida (este componente é o dono do stream) -----
  const streamRef = useRef(null);
  const montadoRef = useRef(true);
  const abrindoRef = useRef(false); // trava reentrância do toque duplo
  const facingRef = useRef("environment");
  // Cada aquisição carrega um número de geração. Fechar a câmera, abrir a
  // galeria ou pedir outra aquisição incrementam esse número — e a aquisição
  // que resolver com número velho MATA o próprio stream em vez de virar órfã.
  // Sem isso, dois toques no botão de virar a câmera deixam um MediaStream
  // vivo para sempre (LED aceso, "câmera em uso" nos outros apps).
  const geracaoRef = useRef(0);
  const [stream, setStream] = useState(null);
  const [abrindoCamera, setAbrindoCamera] = useState(false);
  const [trocandoCamera, setTrocandoCamera] = useState(false);
  const [podeTrocarCamera, setPodeTrocarCamera] = useState(false);

  const pararStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // Rede de segurança: desmontar (trocar de passo no wizard, sair da rota,
  // sessão cair) sempre encerra a câmera.
  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
      pararStream();
    };
  }, [pararStream]);

  /**
   * Adquire o stream do `facing` pedido. Encerra o anterior ANTES — a maioria
   * dos Android não entrega duas capturas simultâneas e devolveria
   * NotReadableError. Devolve null se o componente desmontou no meio.
   */
  const adquirirStream = useCallback(async (facing) => {
    const geracao = ++geracaoRef.current;
    pararStream();
    const novo = await navigator.mediaDevices.getUserMedia(
      restricoesDeVideo(facing),
    );
    // Desmontou, ou alguém pediu outra coisa enquanto esperávamos: este stream
    // não é mais o corrente e ninguém mais teria referência para encerrá-lo.
    if (!montadoRef.current || geracao !== geracaoRef.current) {
      novo.getTracks().forEach((t) => t.stop());
      return null;
    }
    streamRef.current = novo;
    facingRef.current = facing;
    setStream(novo);
    return novo;
  }, [pararStream]);

  const fecharCamera = useCallback(() => {
    geracaoRef.current += 1; // invalida aquisição em voo
    pararStream();
    setStream(null);
  }, [pararStream]);

  const restantes = max - fotos.length;

  const aoEscolher = async (evento) => {
    const arquivos = Array.from(evento.target.files || []).slice(0, restantes);
    evento.target.value = "";
    if (!arquivos.length) return;
    setAvisoCamera(""); // a foto entrou: o aviso da câmera não faz mais sentido
    setProcessando(true);
    try {
      const novas = [];
      for (const arquivo of arquivos) {
        try {
          novas.push({ uid: gerarUid(), imagem: await comprimirFoto(arquivo), legenda: "" });
        } catch {
          toast.error("Não foi possível processar uma das fotos.");
        }
      }
      if (novas.length) onChange([...fotos, ...novas]);
    } finally {
      setProcessando(false);
    }
  };

  const abrirGaleria = () => {
    setAvisoCamera("");
    geracaoRef.current += 1; // desistiu da câmera: aquisição em voo vira lixo
    inputGaleriaRef.current?.click();
  };

  /** Caminho explícito para a câmera do sistema (flash, foco por toque, HDR). */
  const usarCameraDoSistema = () => {
    setAvisoCamera("");
    geracaoRef.current += 1;
    inputCameraRef.current?.click();
  };

  /**
   * Cascata do botão "Tirar foto":
   *   1. câmera embutida (getUserMedia) — faz o Android pedir a permissão de
   *      forma visível e recuperável;
   *   2. permissão negada -> galeria + aviso explicando (insistir no caminho
   *      nativo bateria na MESMA permissão);
   *   3. outros erros (sem câmera / ocupada) -> tenta o `<input capture>`.
   */
  const tirarFoto = async () => {
    if (abrindoRef.current || stream) return; // toque duplo / já aberta
    setAvisoCamera("");
    if (!cameraEmbutidaDisponivel()) {
      inputCameraRef.current?.click();
      return;
    }
    abrindoRef.current = true;
    setAbrindoCamera(true);
    try {
      // Adquire ANTES de abrir o overlay: assim uma negativa de permissão não
      // deixa uma tela preta na frente do usuário.
      const novo = await adquirirStream(facingRef.current);
      if (!novo) return;
      // Só oferece "virar a câmera" se o aparelho tiver mais de uma.
      navigator.mediaDevices
        .enumerateDevices()
        .then((ds) => {
          if (!montadoRef.current) return;
          setPodeTrocarCamera(
            ds.filter((d) => d.kind === "videoinput").length > 1,
          );
        })
        .catch(() => {});
    } catch (e) {
      if (!montadoRef.current) return;
      setAvisoCamera(mensagemDeErroCamera(e));
      if (ehPermissaoNegada(e)) {
        // Melhor esforço: se a ativação do toque ainda valer, o seletor abre
        // sozinho; se o navegador bloquear, o aviso abaixo tem o botão.
        inputGaleriaRef.current?.click();
      } else {
        inputCameraRef.current?.click();
      }
    } finally {
      abrindoRef.current = false;
      if (montadoRef.current) setAbrindoCamera(false);
    }
  };

  /**
   * Vira a câmera. Como o stream antigo precisa morrer antes, uma falha aqui
   * deixaria o overlay sem imagem — então voltamos para a câmera anterior.
   */
  const trocarCamera = async () => {
    if (abrindoRef.current) return; // trava o duplo toque (vazava stream)
    const anterior = facingRef.current;
    const alvo = anterior === "environment" ? "user" : "environment";
    abrindoRef.current = true;
    setTrocandoCamera(true);
    try {
      await adquirirStream(alvo);
    } catch {
      try {
        await adquirirStream(anterior);
        toast.error("Não foi possível trocar de câmera.");
      } catch (e2) {
        fecharCamera();
        setAvisoCamera(mensagemDeErroCamera(e2));
      }
    } finally {
      abrindoRef.current = false;
      if (montadoRef.current) setTrocandoCamera(false);
    }
  };

  const aoCapturar = (video) => {
    if (restantes <= 0) { // defesa: nenhum caminho deveria chegar aqui no limite
      fecharCamera();
      return;
    }
    let dataUrl;
    try {
      dataUrl = comprimirFonte(video, video.videoWidth, video.videoHeight);
    } catch {
      toast.error("Não foi possível salvar a foto. Tente de novo.");
      return;
    }
    fecharCamera();
    setAvisoCamera("");
    onChange([...fotos, { uid: gerarUid(), imagem: dataUrl, legenda: "" }]);
  };

  const remover = (uid) => {
    onChange(fotos.filter((f) => f.uid !== uid));
  };

  const rotuloCamera = () => {
    if (abrindoCamera) return "Abrindo câmera…";
    if (processando) return "Processando…";
    return "Tirar foto";
  };

  return (
    <div>
      <FotosWrap>
        {fotos.map((foto) => (
          <FotoThumbBox key={foto.uid}>
            <img src={foto.imagem} alt={foto.legenda || "Foto da solicitação"} />
            <FotoRemoveBtn
              type="button"
              aria-label="Remover foto"
              onClick={() => remover(foto.uid)}
            >
              <FiX size={15} />
            </FotoRemoveBtn>
          </FotoThumbBox>
        ))}
        <FotoAddBtn
          type="button"
          disabled={restantes <= 0 || processando || abrindoCamera}
          onClick={tirarFoto}
        >
          <FiCamera size={17} />
          {rotuloCamera()}
        </FotoAddBtn>
        <FotoAddBtn
          type="button"
          disabled={restantes <= 0 || processando}
          onClick={abrirGaleria}
        >
          <FiImage size={17} />
          {processando ? "Processando…" : "Galeria"}
        </FotoAddBtn>
      </FotosWrap>

      {avisoCamera && (
        <AvisoCamera>
          <span>{avisoCamera}</span>
          <button type="button" onClick={abrirGaleria}>
            Escolher da galeria
          </button>
        </AvisoCamera>
      )}

      <Hint style={{ marginTop: 6 }}>
        {restantes > 0 ? (
          <>
            {`Até ${max} foto(s) — restam ${restantes}. Tire na hora ou escolha da galeria. `}
            {/* Saída para quem precisa de flash/foco por toque: a câmera
                embutida não tem esses recursos, a do sistema tem. */}
            <LinkCameraSistema type="button" onClick={usarCameraDoSistema}>
              Usar a câmera do celular
            </LinkCameraSistema>
          </>
        ) : (
          "Limite de fotos atingido."
        )}
      </Hint>

      {/* Fallback: navegadores sem getUserMedia, ou falha que não seja de
          permissão. capture=environment pede a câmera traseira ao sistema. */}
      <input
        ref={inputCameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={aoEscolher}
      />
      {/* Galeria: SEM capture o navegador abre o seletor de arquivos/galeria;
          multiple permite anexar várias de uma vez (limitado a `restantes`).
          Este caminho NAO exige permissao do sistema — é a rede de segurança
          quando a câmera está bloqueada. */}
      <input
        ref={inputGaleriaRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={aoEscolher}
      />

      {stream && (
        <CameraCaptura
          stream={stream}
          podeTrocar={podeTrocarCamera}
          trocando={trocandoCamera}
          onTrocarCamera={trocarCamera}
          onCapturar={aoCapturar}
          onFechar={fecharCamera}
        />
      )}
    </div>
  );
}
